import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/lang";

const ChapterImage = ({ src, alt, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);
  return (
    <div ref={ref} className="relative overflow-hidden rounded-md border border-amber-500/15 h-64 sm:h-80 lg:h-96" style={{ clipPath: "polygon(0 0, 100% 0, 100% 92%, 92% 100%, 0 100%)" }}>
      <motion.img src={src} alt={alt} data-testid={testid} style={{ y, scale: 1.18 }} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
    </div>
  );
};

const Services = () => {
  const { t } = useLang();
  const chapters = t.services?.chapters || [];

  return (
    <section id="services" data-testid="services-section" className="relative py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <p data-testid="services-eyebrow" className="text-xs uppercase tracking-[0.3em] font-mono text-amber-700/90 mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-amber-500/60 inline-block" />
            {t.services?.eyebrow}
          </p>
          <h2 data-testid="services-title" className="font-serif text-2xl sm:text-3xl lg:text-5xl tracking-tight text-ivory max-w-2xl leading-[1.1]">
            {t.services?.title}
          </h2>
        </Reveal>

        <div className="mt-16 lg:mt-24 flex flex-col gap-20 lg:gap-28">
          {chapters.map((c, i) => (
            <div key={c.n} data-testid={`service-chapter-${c.n}`} className={`grid lg:grid-cols-12 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? "" : ""}`}>
              <Reveal className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`} delay={0.1}>
                <ChapterImage src={c.img} alt={c.title} testid={`service-image-${c.n}`} />
              </Reveal>
              <Reveal className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`} delay={0.2}>
                <div className="flex items-baseline gap-6">
                  <span className="font-serif italic text-6xl lg:text-8xl text-champagne/25 leading-none select-none">{c.n}</span>
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl lg:text-4xl text-ivory tracking-tight">{c.title}</h3>
                    <p className="text-xs uppercase tracking-[0.25em] font-mono text-amber-700/80 mt-2">{c.place}</p>
                  </div>
                </div>
                <div className="gold-hairline w-full my-6" />
                <p className="text-base leading-relaxed text-zinc-600 max-w-xl">{c.text}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
